import React, { useState } from 'react';
import { 
  ChevronRight, 
  Users, 
  Zap, 
  Brain, 
  CheckCircle, 
  Search, 
  Award, 
  Milestone,
  Database,
  Globe,
  Target,
  Layers,
  Workflow,
  Rocket,
  MessageCircle,
  Shield
} from 'lucide-react';

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const features = [
    {
      icon: <Brain className="w-12 h-12 text-purple-600" />,
      title: "Intelligent Matching",
      description: "Advanced AI algorithms analyze job requirements and candidate profiles to identify the most suitable matches with unprecedented accuracy."
    },
    {
      icon: <Workflow className="w-12 h-12 text-green-600" />,
      title: "Streamlined Workflow",
      description: "Automate and optimize your entire recruitment process from job posting to candidate selection with intelligent pipeline management."
    },
    {
      icon: <Shield className="w-12 h-12 text-blue-600" />,
      title: "Comprehensive Assessment",
      description: "Go beyond resumes with integrated assessments for skills, language proficiency, emotional intelligence, and cultural fit."
    }
  ];

  const problemSolutions = [
    {
      problem: "Inefficient Screening",
      solution: "AI-powered candidate evaluation reduces screening time by up to 70%",
      icon: <Zap className="w-10 h-10 text-blue-500" />
    },
    {
      problem: "Bias in Hiring",
      solution: "Objective, data-driven candidate assessment minimizes unconscious bias",
      icon: <CheckCircle className="w-10 h-10 text-green-500" />
    },
    {
      problem: "Missed Talent",
      solution: "Advanced matching algorithms uncover hidden potential beyond traditional metrics",
      icon: <Search className="w-10 h-10 text-purple-500" />
    }
  ];

  const integrations = [
    { name: "Gmail", icon: "https://upload.wikimedia.org/wikipedia/commons/4/4e/Gmail_Icon.png" },
    { name: "LinkedIn", icon: "https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png" },
    { name: "Monster", icon: "/images/logos/monster.webp" },
    { name: "Indeed", icon: "/images/logos/indeed.jpeg" }
  ];
  

  const pricingPlans = [
    {
      name: "Starter",
      price: "$49/month",
      features: [
        "Up to 50 candidate evaluations",
        "Basic AI screening",
        "Email support"
      ]
    },
    {
      name: "Professional",
      price: "$99/month",
      features: [
        "Up to 200 candidate evaluations",
        "Advanced AI matching",
        "Priority support",
        "Emotional intelligence assessment"
      ],
      recommended: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      features: [
        "Unlimited candidate evaluations",
        "Full AI suite",
        "Dedicated account manager",
        "Custom integrations"
      ]
    }
  ];

  return (
    <div className="pt-16">
        
    <div className="bg-white">
    
      {/* Hero Section - Enhanced */}
      <div className="relative bg-gradient-to-br from-blue-50 to-white">
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 lg:pt-24">
          <div className="text-center">
            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
              Transforming Recruitment with <br />
              <span className="text-blue-600">Intelligent AI Hiring</span>
            </h1>
            <p className="mt-3 max-w-md mx-auto text-lg text-gray-500 sm:text-xl md:mt-5 md:max-w-3xl">
              Revolutionize your hiring process with AI-driven candidate matching, 
              comprehensive assessments, and data-powered insights that uncover 
              exceptional talent.
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <a 
                href="/demo" 
                className="inline-flex items-center px-6 py-3 border border-2 border-blue-600 text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Request Demo
                <Rocket className="ml-3 -mr-1 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
        </div>
        </div>
      </div>

      {/* Problem & Solution Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Solving Recruitment Challenges
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Transforming Hiring Pain Points
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {problemSolutions.map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all">
                <div className="flex items-center mb-4">
                  {item.icon}
                  <h3 className="ml-4 text-xl font-semibold text-gray-800">{item.problem}</h3>
                </div>
                <p className="text-gray-600">{item.solution}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section - Enhanced */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Powerful Features
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              AI-Powered Recruitment Intelligence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-lg transition-all">
                <div className="flex justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Integrations Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-12">
            Seamless Integrations
          </h2>
          <div className="flex justify-center space-x-12 items-center transition-all">
            {integrations.map((integration, index) => (
              <div key={index} className="flex flex-col items-center">
                <img 
                  src={integration.icon} 
                  alt={integration.name} 
                  className="w-20 h-20 mb-4"
                />
                <span className="text-gray-700">{integration.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section 
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-12">
            Flexible Pricing Plans
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`
                  p-6 rounded-lg border-2 
                  ${plan.recommended 
                    ? 'border-blue-500 bg-blue-50 transform scale-105' 
                    : 'border-gray-200'}
                `}
              >
                <h3 className="text-xl font-semibold mb-4">{plan.name}</h3>
                <div className="text-3xl font-bold text-gray-900 mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="text-gray-600">
                      {feature}
                    </li>
                  ))}
                </ul>
                <a 
                  href="#" 
                  className={`
                    inline-block w-full py-3 rounded-md 
                    ${plan.recommended 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                  `}
                >
                  Choose Plan
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
*/}
      {/* CTA Section */}
      <div className="bg-blue-600">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8 lg:py-24 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Ready to Transform Your Hiring Process?
          </h2>
          <p className="mt-4 text-xl text-blue-100 max-w-3xl mx-auto">
            Experience the power of AI-driven recruitment. Schedule a personalized demo 
            or start your free trial today.
          </p>
          <div className="mt-8 flex justify-center space-x-4">
            <a 
              href="/demo" 
              className="inline-flex items-center px-8 py-3 border border-2 border-white text-base font-medium rounded-md text-white hover:bg-blue-700 focus:outline-none"
            >
              Request Demo
              <MessageCircle className="ml-3 -mr-1 h-5 w-5" />
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-white text-base mb-4">
            © 2025 Prodigium Recruit. All rights reserved.
          </div>
          {/*
          <div className="space-x-4">
            <a href="#" className="text-gray-300 hover:text-white">Privacy Policy</a>
            <a href="#" className="text-gray-300 hover:text-white">Terms of Service</a>
            <a href="#" className="text-gray-300 hover:text-white">Contact Us</a>
          </div>
          */}
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;